import { EmbeddableWorkbookSubplaceWithoutRouter } from '@tableau/data-catalog-client-components';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { ISanitizer } from '~commonInfra/utils/Sanitize';

import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '../../app/spaces/SpaceTelemetry';
import { explorerSubspace, newExtractRefreshButton } from '../../app/spaces/SubspaceHelpers';
import { Subspace, TabbedSpace } from '../../app/spaces/TabbedSpace';
import { ISpaceInfoTypeItemCount } from '../../app/state/ContentCountsState';
import { SpaceInfo, SubspaceName, WorkbookSubspaceName } from '../../app/state/SpaceInfo';
import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { Messages } from '../../codegen/Localize';
import { CustomViewDefinition } from '../../contentDefinitions/CustomViewDefinition';
import { ExtractTaskDefinition } from '../../contentDefinitions/ExtractTaskDefinition';
import { SubscriptionDefinition } from '../../contentDefinitions/SubscriptionDefinition';
import { ViewDefinition } from '../../contentDefinitions/ViewDefinition';
import { DatasourceDefinition } from '../../datasource/components/contentDefinitions/DatasourceDefinition';
import { DraftWorkbookSpaceProps } from '../../drafts/types/DraftsTypes';
import { MetricDefinition } from '../../metrics/models/MetricDefinition';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { FilterFieldNames } from '../../ts/ServerApi/types/IFilterClause';
import { DataCatalogUIActionsService } from '../../ts/services/DataCatalogUIActionsService';
import { IWorkbookDetailInfoWithUsage } from '../models/IWorkbookInfo';
import { IWorkbookActions } from '../ServerApi/types/IWorkbookActions';
import { WorkbookSpaceHeader } from './WorkbookSpaceHeader';

export interface WorkbookSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
  readonly contentCounts: ISpaceInfoTypeItemCount<'workbook'>;
  readonly contentCountsUptoDate: boolean;
  readonly areUsersVisible: boolean;
  readonly draft?: DraftWorkbookSpaceProps;
}

export interface WorkbookSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface WorkbookSpaceOwnProps {
  readonly workbookDetail: IWorkbookDetailInfoWithUsage;
  readonly workbookActions: IWorkbookActions;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly sanitizer: ISanitizer;
}

export type WorkbookSpaceProps = WorkbookSpaceStateProps & WorkbookSpaceOwnProps & WorkbookSpaceDispatchProps;

function lineageSubspace(
  workbook: IWorkbookDetailInfoWithUsage,
  siteUrls: SpacesSiteUrls,
  areUsersVisible: boolean
): Subspace {
  return {
    name: 'lineage',
    tabLabel: Messages.dataCatalogLineage(),
    shouldRenderTabContentCount: false,
    renderContent: () =>
      <EmbeddableWorkbookSubplaceWithoutRouter
        vizportalUrlId={workbook.id}
        client={catalogApolloClient}
        siteUrls={siteUrls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        areUsersVisible={areUsersVisible}
        featureFlags={catalogFeatureFlagsAdapter()}
      />
  };
}

export const getWorkbookUrl = (urls: SpacesSiteUrls, workbookId: string, subspace?: WorkbookSubspaceName) => {
  if (subspace) {
    const WorkbookSubspacesToUrl: Record<WorkbookSubspaceName, string> = {
      views: urls.workbookViewsUrl(workbookId),
      datasources: urls.workbookDatasourcesUrl(workbookId),
      dataconnections: (urls.workbookDatasourcesUrl(workbookId) + '?' + FilterFieldNames.contentType + '=' + 'dataconnection'),
      connectedMetrics: urls.workbookConnectedMetricsUrl(workbookId),
      customViews: urls.workbookCustomViewsUrl(workbookId),
      extractTasks: urls.workbookExtractRefreshesUrl(workbookId),
      subscriptions: urls.workbookSubscriptionsUrl(workbookId),
      lineage: urls.workbookLineageUrl(workbookId),
      workbookAccelerations: urls.workbookAccelerationsUrl(workbookId)
    };

    const url = WorkbookSubspacesToUrl[subspace];
    if (url) {
      return url;
    }
  }

  return urls.workbookUrl(workbookId);
};

function getWorkbookSubspaces(
  workbook: IWorkbookDetailInfoWithUsage,
  workbookActions: IWorkbookActions,
  contentCounts: ISpaceInfoTypeItemCount<'workbook'>,
  contentCountsUpToDate: boolean,
  siteUrls: SpacesSiteUrls,
  catalogingOnSiteEnabled: boolean,
  areUsersVisible: boolean
): ReadonlyArray<Subspace> {

  // When User Visibility is limited and the user does not have permissions to view other
  //  users, Workbook's owner shows up as Hidden User. Under the same conditions, we need
  //  to disable subscriptions tab, because user can't see any subscriptions in the workbook
  //  space.
  let showSubscriptionsTab: boolean = !workbook.owner.isHiddenInternalUser;

  return _.compact([
    explorerSubspace({
      name: 'views',
      tabLabel: Messages.views(),
      supportedDisplayModes: ViewDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['views'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled'
    }),
    explorerSubspace({
      name: 'datasources',
      // TFSID:855453 This string has correct capitalization and avoids adding new string past loc change for next release (19.2)
      tabLabel: Messages.publishWorkbookSuccessDialogText_datasourcesHeader(),
      supportedDisplayModes: DatasourceDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['datasources'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.workbooksDatasourcesEmptyMessage()
      }
    }),
    FeatureFlags.metricsSpaceEnabled && explorerSubspace({
      name: 'connectedMetrics',
      tabLabel: Messages.connectedMetrics(),
      supportedDisplayModes: MetricDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['connectedMetrics'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled'
    }),
    explorerSubspace({
      name: 'customViews',
      tabLabel: Messages.customViews(),
      supportedDisplayModes: CustomViewDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['customViews'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.workbooksCustomViewsEmptyMessage()
      }
    }),
    workbook.hasExtracts && explorerSubspace({
      name: 'extractTasks',
      tabLabel: Messages.extractTasks(),
      supportedDisplayModes: ExtractTaskDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['extractTasks'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarContentSpecificControls: {
        beginning: newExtractRefreshButton(workbook, workbookActions)
      },
      toolbarVisibility: 'enabled'
    }),
    showSubscriptionsTab && explorerSubspace({
      name: 'subscriptions',
      tabLabel: Messages.subscriptions(),
      supportedDisplayModes: SubscriptionDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['subscriptions'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.workbooksSubscriptionsEmptyMessage()
      }
    }),
    catalogingOnSiteEnabled && lineageSubspace(
      workbook,
      siteUrls,
      areUsersVisible
    ),
  ]);
}

export class WorkbookSpace extends React.PureComponent<WorkbookSpaceProps> {

  private memoizedGetWorkbookSubspaces = defaultMemoize(getWorkbookSubspaces);

  public componentDidUpdate(prevProps: WorkbookSpaceProps): void {
    this.fetchUpdatedCounts();
    if (prevProps.workbookDetail.id !== this.props.workbookDetail.id) {
      maybeSendTelemetry_favoriteItemOpened(this.props.workbookDetail);
    }
  }

  public componentDidMount(): void {
    this.fetchUpdatedCounts();
    maybeSendTelemetry_favoriteItemOpened(this.props.workbookDetail);
  }

  public exploreWorkbookUrl = (subspaceName?: WorkbookSubspaceName) => {
    return getWorkbookUrl(this.props.urls, this.props.workbookDetail.id, subspaceName);
  }

  public render() {
    const subspaces = this.memoizedGetWorkbookSubspaces(
      this.props.workbookDetail,
      this.props.workbookActions,
      this.props.contentCounts,
      this.props.contentCountsUptoDate,
      this.props.urls,
      this.props.catalogingOnSiteEnabled,
      this.props.areUsersVisible
    );

    const rawSubspaceName = this.props.spaceInfo.subspaceName;
    const subspaceTabName: SubspaceName = rawSubspaceName === 'dataconnections' ? 'datasources' : rawSubspaceName;

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={subspaceTabName}
        urlForSubspace={this.exploreWorkbookUrl}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'workbook'}
        spaceItemName={this.props.workbookDetail.name}
      />
    );
  }

  private mastheadElement(): React.ReactNode {
    return <WorkbookSpaceHeader
      item={this.props.workbookDetail}
      availableActions={this.props.workbookActions}
      openDetail={this.props.openDetail}
      addToFavorites={this.props.addToFavorites}
      removeFromFavorites={this.props.removeFromFavorites}
      sanitizer={this.props.sanitizer}
      urls={this.props.urls}
      catalogingOnSiteEnabled={this.props.catalogingOnSiteEnabled}
      draft={this.props.draft}
    />;
  }

  private fetchUpdatedCounts = (): void => {
    if (this.props.fetchUpdatedContentCounts) {
      this.props.fetchUpdatedContentCounts();
    }
  }
}
